import React from "react";
import { useTheme } from "react-jss";
import classnames from "classnames";
import { RendererProps } from "../BaseRenderer";
import { getSettingString, hasSetting } from "../../../models/utilities/settingUtilities";
import { AxisModel } from "../../../models/AxisModel";
import { ValidationResultModel } from "../../../models/ValidationResultModel";
import { ChoiceModel } from "../../../models/ChoiceModel";
import useStyle from "./GridCellRenderer.styles";
import { QuestionType } from "../../../models/enums/QuestionType";
import type { Theme } from "../../../types/Theme";
import { useModernGridRowStripe } from "../../../hooks/ModernGridRowStripeContext";
import { RowType } from "../../../models/enums/RowType";
import {
    isModernGridTableQuestion,
    isModernHybridGridTable,
    resolveModernGridSubHeaderColor,
    useModernGridTableStyles,
} from "../../../theme/gridThemeTokens";

interface Props extends RendererProps {
    isColumnHeader?: boolean;
    isChoiceHeader?: boolean;
    isRowHeader?: boolean;
    row?: AxisModel;
    column?: AxisModel;
    colSpan?: number;
    validationResult?: ValidationResultModel;
    choice?: ChoiceModel;
    columnIndex?: number;
}

enum HeaderType {
    Choice,
    Column,
    Row,
}

const headerProps = {
    [HeaderType.Choice]: {
        role: "columnheader",
        scope: "col",
    },
    [HeaderType.Column]: {
        role: "columnheader",
        scope: "col",
    },
    [HeaderType.Row]: {
        role: "rowheader",
        scope: "row",
    },
};

export default function GridCellRenderer(props: Props) {
    const {
        row,
        column,
        validationResult,
        isChoiceHeader,
        isColumnHeader,
        isRowHeader,
        colSpan,
        children,
        choice,
        question,
    } = props;
    const theme = useTheme() as Theme;
    const gridQuestion = question?.parent ?? question;
    const useModernHybridGridCellPadding = isModernHybridGridTable(gridQuestion?.type, theme);
    const classes = useStyle({ theme, useModernHybridGridCellPadding });
    const isStripedRow = useModernGridRowStripe();
    const isModernGridTable =
        isModernGridTableQuestion(gridQuestion?.type) && useModernGridTableStyles(theme);
    const inputSize = getSettingString(column, "Size");

    const rowColor = hasSetting(row, "Color") ? getSettingString(row, "Color") : null;
    const columnColor = hasSetting(column, "Color") ? getSettingString(column, "Color") : null;
    const choiceColor = hasSetting(choice, "Color") ? getSettingString(choice, "Color") : null;
    const isStarRatting = question?.type === QuestionType.StarRatingGrid;
    const hasAxisFillColor = Boolean(rowColor || columnColor || choiceColor);
    const isRunningTotalModern =
        question?.type === QuestionType.RunningTotal && theme.UseModernTheme === true;
    const gridItemClass = classnames(
        classes.gridItem,
        isRunningTotalModern && classes.gridItemRunningTotal,
        hasAxisFillColor && classes.gridItemAxisFill,
        isModernGridTable && isStripedRow && !hasAxisFillColor && classes.gridItemModernStriped,
    );

    const style = {
        backgroundColor: hasAxisFillColor ? rowColor || columnColor || choiceColor : undefined,
        width: inputSize === "Full" ? "100%" : undefined,
        display: isRowHeader && isStarRatting ? "table-cell" : null,
    };

    let headerClass: string;
    let headerType: HeaderType;
    const attributes = {};

    if (isChoiceHeader) {
        headerType = HeaderType.Choice;
        const isHybridSubHeaderChoiceCell =
            question?.type === QuestionType.HybridGrid &&
            row?.type === RowType.Text &&
            isModernHybridGridTable(question?.type, theme);
        headerClass = classnames(
            classes.gridChoiceHeader,
            isHybridSubHeaderChoiceCell && classes.gridHybridSubHeaderChoiceHeader,
        );
        if (choice && choice.index >= 0) {
            const questionName = question?.name ?? "";
            const columnIndex = column?.index ?? 0; // Hybrid choice will have 2 level of header

            if (question?.type === QuestionType.HybridGrid) {
                attributes["id"] = `Q_${questionName}_MAINCOLHEADER${
                    columnIndex + 1
                }_CHOICEHEADER_${choice.index + 1}`;
            } else {
                attributes["id"] = `Q_${questionName}_CHOICEHEADER_${choice.index + 1}`;
            }
        }
    } else if (isColumnHeader) {
        headerType = HeaderType.Column;
        headerClass = classes.gridColumnHeader;
    } else if (isRowHeader) {
        headerType = HeaderType.Row;
        headerClass = classnames(
            classes.gridRowHeader,
            question?.type === QuestionType.RunningTotal && theme.UseModernTheme === true
                ? classes.gridRowHeaderRunningTotal
                : undefined,
        );
    } else {
        return (
            <td
                className={gridItemClass}
                colSpan={colSpan}
                style={style}
                role="cell"
            >
                {children}
            </td>
        );
    }

    const { role, scope } = headerProps[headerType];
    return (
        <th
            className={classnames(gridItemClass, headerClass)}
            colSpan={colSpan}
            role={role}
            scope={scope}
            style={style}
            {...attributes}
        >
            {children}
        </th>
    );
}
